import {
  computed,
  inject,
  Injectable,
  linkedSignal,
  resource,
  Signal,
  signal,
} from '@angular/core';
import { ProductRepository } from '../repositories/product.repository';
import { CartItem } from '@softapps/wws/pos-core';

export class BaseProductService {
  /* PRIVATE API */
  readonly #categories = resource({
    params: () => ({}),
    loader: async () => {
      return this.productRepository.loadCategories(this.returnableProducts);
    },
  });

  readonly #categoryImageResource = resource({
    params: () => this.#categoryId(),
    loader: async (params) => {
      const categoryId = params.params;
      if (!categoryId) {
        return null;
      }
      const categoryImageMap = this.#categoryImages();
      if (categoryImageMap.has(categoryId)) {
        return categoryImageMap.get(categoryId);
      }
      const image = await this.productRepository.loadCategoryImageById(categoryId);
      categoryImageMap.set(categoryId, image);
      this.#categoryImages.set(new Map(categoryImageMap));
      return image;
    },
  });

  readonly #productsResource = resource({
    params: () => this.#categoryId(),
    loader: async (params) => {
      const categoryId = params.params;
      if (!categoryId) {
        return null;
      }

      const result = await this.productRepository.loadProductsByCategoryId(categoryId);
      this.#products.set(result);
      return result;
    },
  });

  readonly #allProductsResource = resource({
    params: () => this.categories(),
    loader: async (params) => {
      const categories = params.params;
      if (!categories) {
        return [];
      }

      const itemsArray = await Promise.all(
        categories.map((category) =>
          this.productRepository.loadProductsByCategoryId(
            category.id,
            this.returnableProducts
          )
        )
      );

      const allProducts = itemsArray.flatMap((o) => o);
      this.#allProducts.set(allProducts);
      return allProducts;
    },
  });

  readonly #productImageResource = resource({
    params: () => this.#productId(),
    loader: async (params) => {
      const productId = params.params;
      if (!productId) {
        return null;
      }
      const productImageMap = this.#productImages();
      if (productImageMap.has(productId)) {
        return productImageMap.get(productId);
      }
      try {
        const image = await this.productRepository.loadProductImageById(productId);
        productImageMap.set(productId, image);
        this.#productImages.set(new Map(productImageMap));
        return image;
      } catch {
        return null;
      }
    },
  });

  /* ids */
  readonly #productId = signal<number | undefined>(undefined);
  readonly #categoryId = signal<number | undefined>(undefined);

  /* data */
  readonly #categoryImages = signal<Map<number, string>>(new Map());
  readonly #products = signal<CartItem[]>([]);
  readonly #allProducts = signal<CartItem[]>([]);
  readonly #productImages = signal<Map<number, string>>(new Map());

  /* PUBLIC API */
  readonly categoriesLoading = computed(() => this.#categories.isLoading());
  readonly categoryImagesLoading = computed(() =>
    this.#categoryImageResource.isLoading()
  );
  readonly productsLoading = computed(() => this.#productsResource.isLoading());
  readonly productImagesLoading = computed(() => this.#productImageResource.isLoading());
  readonly allProductsLoading = computed(() => this.#allProductsResource.isLoading());

  // Reading `.value()` on an errored resource THROWS — a throw inside a computed that
  // feeds a resource `params` retries forever. Gate every read on hasValue().
  readonly categoriesError = computed(() => this.#categories.status() === 'error');
  readonly productsError = computed(() => this.#productsResource.status() === 'error');
  readonly allProductsError = computed(
    () => this.#allProductsResource.status() === 'error'
  );

  // Signals with data
  readonly categories = computed(() =>
    this.#categories.hasValue()
      ? (this.#categories.value() ?? []).sort((a, b) => a.sortNumber - b.sortNumber)
      : []
  );

  readonly products = linkedSignal(() =>
    this.#productsResource.hasValue() ? (this.#productsResource.value() ?? []) : []
  );

  readonly allProducts = linkedSignal(() => {
    return this.#allProducts();
  });

  constructor(
    protected productRepository: ProductRepository,
    protected returnableProducts = false
  ) {}

  readonly categoryImage = (categoryId: number): Signal<string | null> => {
    return computed(() => {
      const imageMap = this.#categoryImages();
      return imageMap.get(categoryId) ?? null;
    });
  };

  // unused
  readonly productImage = (productId: number): Signal<string | null> => {
    return computed(() => {
      const imageMap = this.#productImages();
      return imageMap.get(productId) ?? null;
    });
  };

  // Loading methods
  loadProducts(categoryId: number) {
    this.#categoryId.set(categoryId);
  }
  loadCategoryImage(categoryId: number) {
    this.#categoryId.set(categoryId);
  }
  loadProductImage(productId: number) {
    this.#productId.set(productId);
  }

  // Search methods
  searchProducts(value: string | null | undefined): void {
    const products = this.#productsResource.hasValue()
      ? (this.#productsResource.value() ?? [])
      : [];
    if (!value) {
      this.products.set(products);
      return;
    }

    const searchText = value.toLowerCase();
    const result = products.filter((item) => {
      return item.product.description?.description.toLowerCase().includes(searchText);
    });

    this.products.set(result);
    return;
  }
}

@Injectable()
export class ProductService extends BaseProductService {
  constructor() {
    super(inject(ProductRepository), false);
  }
}

@Injectable()
export class ReturnableProductService extends BaseProductService {
  constructor() {
    super(inject(ProductRepository), true);
  }
}
